<template>
	<div v-show="!changeSuccessful">
		<form
			v-if="!showCode"
			@submit.prevent="resetPassword()">
			<div>
				<tw-input
					v-model="email"
					id="email"
					:required="true"
					type="email"
					autocomplete="email"
					label="Email address"
					validation="Please enter your email address."
					:error="emailError" />
			</div>
		</form>
		<div 
			v-else
			class="space-y-2">
			<div class="text-sm">
				<p v-if="currentRoute === 'ResetPasswordRequested'">
					You must update your password before logging in.
				</p>
				<p>An email has been sent to <span class="font-bold font-gotham">{{ email }}</span> containing a verification code. Please enter the code and a new password below to finish updating your password.</p>
			</div>
			<div class="w-full">
				<tw-confirmation-code
					v-model="code" />
			</div>
			<div class="text-sm">
				<p>
					Your new password must contain:
				</p>
				<ul class=" list-disc pl-8">
					<li>
						At least 8 characters
					</li>
				</ul>
			</div>
			<fieldset class="mt-6 bg-white dark:bg-gray-900">
				<legend class="block text-sm font-bold leading-6 text-gray-900 dark:text-gray-200">
					New Password
				</legend>
				<div class="mt-2 -space-y-px rounded-md shadow-sm">
					<div>
						<label
							for="password"
							class="sr-only">New password</label>
						<div class="relative">
							<input
								v-model="newPassword"
								:type="showPassword ? 'text' : 'password'"
								name="new-password"
								id="password"
								autocomplete="new-password"
								class="pr-12 relative min-w-0 block w-full rounded-none rounded-t-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-dbBlue sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-500"
								placeholder="New password">
							<div class="flex absolute inset-y-0 right-0">
								<button
									id="show-hide-password"
									tabindex="-1"
									class="pr-3 flex items-center text-gray-300 dark:text-gray-500 z-20"
									@click.prevent="showPassword = !showPassword">
									<EyeIconMini
										v-show="!showPassword"
										class="h-5 w-5"
										aria-hidden="true" 
										tabindex="-1" />
									<EyeSlashIconMini
										v-show="showPassword"
										class="h-5 w-5"
										aria-hidden="true" 
										tabindex="-1" />
								</button>
							</div>
						</div>
					</div>
					<div>
						<label
							for="confirm-password"
							class="sr-only">Confirm new password</label>
						<div class="relative">
							<input
								v-model="confirmNewPassword"
								:type="showPassword ? 'text' : 'password'"
								name="confirm-password"
								id="confirm-password"
								autocomplete="new-password"
								class="pr-12 relative min-w-0 block w-full rounded-none rounded-b-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-dbBlue sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-500"
								placeholder="Confirm new password">
							<div class="flex absolute inset-y-0 right-0">
								<div
									id="password-match-indicator"
									class="pr-3 flex items-center pointer-events-none relative z-20">
									<CheckCircleIconMini
										:class="{'opacity-0': !passwordsMatch}"
										class="h-5 w-5 text-green-500 transition-all duration-300 ease-in-out absolute right-3"
										aria-hidden="true" />
									<CheckCircleIcon
										:class="{'opacity-0': passwordsMatch}"
										class="h-5 w-5 text-gray-300 dark:text-gray-500 transition-all duration-300 ease-in-out absolute right-3"
										aria-hidden="true" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</fieldset>
		</div>
		<div>
			<div class="text-red-600 w-full justify-center text-sm h-16 py-2 items-end flex">
				{{ message }}
			</div>
			<tw-button
				v-show="showCode"
				@click="confirmCode"
				:disabled="loading"
				:loading="loading"
				class="btn-base btn-db-yellow w-full mb-4">
				Submit
			</tw-button>
			<tw-button
				@click="resetPassword"
				class="btn-base w-full"
				:class="[showCode ? 'btn-white' : 'btn-db-yellow']"
				:disabled="loading"
				:loading="loading">
				{{ submitButton }}
			</tw-button>
			<div class="text-center mt-2">
				<router-link
					:to="{name: 'SignIn'}"
					class="link">
					Cancel
				</router-link>
			</div>
		</div>
	</div>
	<div
		v-show="changeSuccessful"
		class="flex flex-col justify-between h-full">
		<div class="space-y-8">
			<div>
				<p class="text-sm mt-2">
					Your password has been successfully updated. Please
					<router-link
						:to="{name: 'SignIn' }"
						class="link">
						Return to the Customer Login screen
					</router-link>
					to log in again.
				</p>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export default { name: 'ForgotPasswordForm' } // name the component
</script>

<script setup lang="ts">
	// api
	import { 
		Auth
	} from 'aws-amplify'

	// components
	import TwConfirmationCode from '@/components/InputGroups/TwConfirmationCode.vue'

	import {
		CheckCircleIcon
	} from '@heroicons/vue/24/outline'

	import {
		CheckCircleIcon as CheckCircleIconMini,
		EyeIcon as EyeIconMini,
		EyeSlashIcon as EyeSlashIconMini
	} from '@heroicons/vue/20/solid'

	// router
	import {
		useRoute
	} from 'vue-router'
	
	// utilities
	import {
		isValidEmail
	} from '@/utilities/email'

	// vue
	import {
		computed,
		onMounted,
		ref,
		watch
	} from 'vue'

	const route = useRoute()

	const currentRoute = computed( () => {
		return route.name
	})

	// sign in passes the email in history state rather than the URL; it survives a reload but not a new tab
	const requestedEmail = currentRoute.value === 'ResetPasswordRequested' ? String(window.history.state?.email ?? "") : ""

	const email = ref(requestedEmail)
	const emailError = ref("")
	const newPassword = ref("")
	const confirmNewPassword = ref("")
	const loading = ref(false)
	const showCode = ref<boolean>(requestedEmail !== "")
	const message = ref<string>('')
	const code = ref<string>('')
	const changeSuccessful = ref<boolean>(false)
	const showPassword = ref<boolean>(false)

	const passwordsMatch = computed(() => {
		return newPassword.value && (newPassword.value === confirmNewPassword.value)
	})

	onMounted(() => {
		if (requestedEmail) {
			resetPassword()
		}
	})

	const submitButton = computed<string>(() => {
		return showCode.value ? 'Resend Code' : 'Submit'
	})

	// hide the format error as soon as the user edits the email again
	watch(email, () => {
		emailError.value = ""
	})

	async function resetPassword() {
		loading.value = true
		message.value = ""

		if (email.value == "") {
			document.getElementById('email')?.focus()
			loading.value = false
			return
		}

		if (!isValidEmail(email.value)) {
			emailError.value = "Please enter a valid email address."
			document.getElementById('email')?.focus()
			loading.value = false
			return
		}

		try {
			await Auth.forgotPassword(email.value)
			showCode.value = true
		}
		catch(error) {
			if(String(error).includes('LimitExceededException')) {
				message.value = "Password reset attempts exceeded. Please try again later."
			}
			else if(String(error).includes('UserNotFoundException')) {
				showCode.value = true
				return
			}
			else if(String(error).includes('Cannot reset password for the user as there is no registered/verified email')){
				message.value = "Password cannot be reset. Your account has not been confirmed. Please return to the customer login and log in again."
			}
			console.error(error)
		}
		finally {
			loading.value = false
		}
	}

	async function confirmCode() {
		try {
			loading.value = true
			message.value = ""

			if(!passwordsMatch.value) {
				throw 'PasswordMismatchException'
			}

			let username = email.value
			let password = confirmNewPassword.value
			await Auth.forgotPasswordSubmit(username, code.value, password)
			showCode.value = false
			changeSuccessful.value = true
		}
		catch (error) {
			if(String(error).includes('CodeMismatchException')) {
				message.value = "Invalid verification code provided. Please try again."
			}
			else if(String(error).includes('InvalidPasswordException')) {
				message.value = "New password does not satisfy requirements. Please try again."
			}
			else if(String(error).includes('ExpiredCodeException')) {
				message.value = "Verification code has expired. Please resend the code using the button below and try again."
			}
			else if(String(error).includes('PasswordMismatchException')) {
				message.value = "Passwords do not match. Please try again."
			}
			console.error(error)
		}
		finally {
			loading.value = false
		}
	}
</script>
